import React, { useState } from 'react';
import {
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  StickyNote2Outlined as NoteIcon,
} from '@mui/icons-material';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const AddNotes = ({
  open,
  onClose,
  leadIds = [],
  onComplete = () => {},
}) => {
  const { addLeadNotes, isLoading } = useLead();
  const { openSuccessModal, openErrorModal } = useModal();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  const isSingleLead = leadIds.length === 1;
  const title = isSingleLead
    ? 'Add notes to lead'
    : `Add notes to ${leadIds.length} leads`;

  const handleSubmit = async () => {
    if (!notes || notes.trim() === '' || leadIds.length === 0) return;

    try {
      setError(null);

      const notesData = {
        lead_ids: leadIds,
        notes: notes.trim(),
      };

      const response = await addLeadNotes(notesData);
      onComplete();
      setNotes('');
      onClose();
      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        if (data.passed.length === leadIds.length) {
          openSuccessModal(
            'Notes added',
            'Notes were added to all selected leads.'
          );
        } else if (data.error.length > 0 && data.error.length < leadIds.length) {
          openSuccessModal(
            'Partial success',
            `Notes added to ${data.passed.length} lead(s); ${data.error.length} failed.`
          );
        }
      } else {
        openErrorModal(
          'Failed to add notes',
          'Could not add notes to the selected lead(s).'
        );
      }
    } catch (err) {
      console.error('Error adding notes:', err);
      setError({ error: true, message: err.message || 'Failed to add notes' });
    }
  };

  const handleClose = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontFamily: T.font,
      fontSize: 13,
      borderRadius: 2,
      bgcolor: T.white,
      '& fieldset': { borderColor: T.borderL },
      '&:hover fieldset': { borderColor: T.border },
      '&.Mui-focused fieldset': { borderColor: T.green },
    },
    '& .MuiInputLabel-root': {
      fontFamily: T.font,
      fontSize: 13,
      color: T.subtle,
      '&.Mui-focused': { color: T.green },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      fontFamily: T.font,
      opacity: 0.7,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <NoteIcon sx={{ fontSize: 20, color: T.green }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontSize: 16,
                  fontWeight: 700,
                  color: T.ink,
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              {leadIds.length !== 1 && (
                <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25 }}>
                  {leadIds.length === 0
                    ? 'No leads selected'
                    : `${leadIds.length} leads selected`}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close"
            sx={{ color: T.muted, '&:hover': { color: T.ink } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={12}
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Type your note. It will be added to the activity timeline for each selected lead."
          variant="outlined"
          autoFocus
          sx={textFieldSx}
        />

        {error && (
          <Box sx={{ mt: 2 }}>
            {error.errored && error.errored.length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${T.redBorder}`,
                  bgcolor: T.redLt,
                }}
              >
                <Typography sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 600, color: T.red, mb: 1 }}>
                  Failed for {error.errored.length} lead(s)
                </Typography>
                <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                  {error.errored.map((item) => (
                    <Typography
                      key={item.id}
                      sx={{ fontFamily: T.font, fontSize: 11.5, color: T.red, display: 'block', mb: 0.5 }}
                    >
                      Lead #{item.id}: {item.message || 'Unknown error'}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {error.error && (
              <Box
                sx={{
                  mt: error.errored?.length ? 1.5 : 0,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${T.redBorder}`,
                  bgcolor: T.redLt,
                }}
              >
                <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>
                  {error.message || 'An error occurred while adding notes'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          pt: 0,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !notes ||
            notes.trim() === '' ||
            leadIds.length === 0 ||
            isLoading
          }
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            bgcolor: T.green,
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} thickness={4} sx={{ color: T.white }} />
              Adding…
            </Box>
          ) : (
            'Add notes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNotes;
